import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Trash2, Eye, Ship, Clock, Ruler } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { navigationApi, type NavSession } from '@/api/endpoints/navigation';
import { RouteReplayMap } from '@/features/reports/RouteReplayMap';
import { cn, formatDateTime } from '@/lib/utils';
import type { Position } from '@/api/types';

export function RoutesPage() {
  const query = useQuery({ queryKey: ['app-routes'], queryFn: () => navigationApi.routes() });
  const routes = query.data ?? [];
  const [openId, setOpenId] = useState<number | null>(null);

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div>
        <h1 className="text-xl font-bold">Rotas do App</h1>
        <p className="text-sm text-muted-foreground">
          Navegações feitas pelo GPS do app (<b>Rota via App</b>) — replay, métricas e histórico.
        </p>
      </div>

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando rotas…</p>
      ) : query.error ? (
        <p className="text-sm text-destructive">Erro ao carregar (faça login).</p>
      ) : routes.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <Ship className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">Nenhuma rota salva. Use o Navegador Premium.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {routes.map((r) => (
            <RouteCard key={r.id} route={r} open={openId === r.id} onToggle={() => setOpenId(openId === r.id ? null : r.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function RouteCard({ route, open, onToggle }: { route: NavSession; open: boolean; onToggle: () => void }) {
  const queryClient = useQueryClient();
  const mins = route.total_time_seconds != null ? Math.round(route.total_time_seconds / 60) : 0;

  const detail = useQuery({
    queryKey: ['app-route', route.id],
    queryFn: () => navigationApi.detail(route.id),
    enabled: open,
  });

  const del = useMutation({
    mutationFn: () => navigationApi.remove(route.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['app-routes'] }),
  });

  const positions = (detail.data?.coords ?? []).map((c) => ({
    latitude: c.latitude, longitude: c.longitude, fixTime: c.recorded_at ?? new Date().toISOString(), speed: c.speed ?? undefined,
  })) as unknown as Position[];

  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-bold">{route.route_name ?? `Navegação #${route.id}`}</p>
            <p className="text-xs text-muted-foreground">{route.started_at ? formatDateTime(route.started_at) : '—'}</p>
          </div>
          <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold', route.origin === 'app' ? 'bg-primary/10 text-primary' : 'bg-status-off/15 text-status-off')}>
            {route.origin === 'app' ? 'Rota via App' : 'Rota via Rastreador'}
          </span>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Ruler className="h-3.5 w-3.5" /> {(route.total_distance_nm ?? 0).toFixed(2).replace('.', ',')} mn</span>
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {mins} min</span>
          {route.average_speed != null && <span>Vel. média {route.average_speed.toFixed(1).replace('.', ',')} nós</span>}
          {route.max_speed != null && <span>Vel. máx {route.max_speed.toFixed(1).replace('.', ',')} nós</span>}
          {route.status === 'active' && <span className="font-bold text-status-running">em andamento</span>}
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onToggle} className={cn('inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold', open ? 'bg-primary text-primary-foreground' : 'bg-secondary hover:bg-accent')}>
            <Eye className="h-3.5 w-3.5" /> {open ? 'Ocultar' : 'Ver no mapa'}
          </button>
          <button type="button" onClick={() => { if (window.confirm('Excluir esta rota?')) del.mutate(); }} className="ml-auto rounded-md p-1.5 text-muted-foreground hover:bg-accent"><Trash2 className="h-4 w-4" /></button>
        </div>
        {open && (detail.isLoading ? (
          <p className="text-xs text-muted-foreground">Carregando trajeto…</p>
        ) : positions.length > 1 ? (
          <RouteReplayMap positions={positions} />
        ) : (
          <p className="text-xs text-muted-foreground">Sem trajeto suficiente para replay.</p>
        ))}
      </CardContent>
    </Card>
  );
}
